import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, ChevronRight, Cloud, Shield, Brain, GitMerge, Boxes, Briefcase } from "lucide-react";
import SEO from "../components/SEO";
import { getService, getNextService } from "../data/services";

const ICONS = { cloud: Cloud, shield: Shield, brain: Brain, "git-merge": GitMerge, boxes: Boxes, briefcase: Briefcase };

const ServiceDetail = () => {
  const { slug } = useParams();
  const s = getService(slug);
  if (!s) return <Navigate to="/services" replace />;
  const next = getNextService(slug);
  const Icon = ICONS[s.icon];
  const NextIcon = ICONS[next.icon];

  return (
    <>
      <SEO title={`${s.title} — ${s.tagline}`} description={s.overview} path={`/services/${s.slug}`} image={s.image} />

      {/* Hero */}
      <section style={{ paddingTop: 64, paddingBottom: 48 }}>
        <div className="container-grav">
          <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32 }}>
            <Link to="/" style={{ color: "inherit", textDecoration: "none" }}>HOME</Link>
            <ChevronRight size={12} />
            <Link to="/services" style={{ color: "inherit", textDecoration: "none" }}>SERVICES</Link>
            <ChevronRight size={12} />
            <span style={{ color: "var(--green)" }}>{s.title.toUpperCase()}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 24 }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--green-bg)", color: "var(--green)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  {Icon && <Icon size={24} strokeWidth={1.75} />}
                </div>
                <span className="eyebrow">{s.code} · Practice</span>
              </div>
              <h1 className="display-xl">{s.title}</h1>
            </div>
            <div className="lg:col-span-4">
              <p className="lede">{s.tagline}</p>
            </div>
          </div>

          <div style={{ marginTop: 64 }}>
            <img src={s.image} alt={s.title} style={{ width: "100%", height: "clamp(280px, 38vw, 480px)", objectFit: "cover", display: "block" }} />
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="section">
        <div className="container-grav">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="eyebrow">Overview</div>
            </div>
            <div className="lg:col-span-8">
              <p className="display-md" style={{ fontWeight: 600, lineHeight: 1.25 }}>{s.overview}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section style={{ background: "var(--bg-alt)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }} className="section">
        <div className="container-grav">
          <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />Key capabilities</div>
          <h2 className="display-lg" style={{ maxWidth: 820, marginBottom: 72 }}>What this practice delivers.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {s.capabilities.map((c, i) => (
              <div key={c.title} className="tile" style={{ minHeight: 220 }}>
                <div className="accent-bar" />
                <div className="eyebrow" style={{ marginBottom: 16 }}>0{i + 1}</div>
                <h3 className="display-sm" style={{ marginBottom: 12 }}>{c.title}</h3>
                <p className="body-md">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="section">
        <div className="container-grav">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />Measurable outcomes</div>
              <h2 className="display-lg">Evidence, <span className="text-green">not promises.</span></h2>
              <ul style={{ listStyle: "none", padding: 0, margin: "40px 0 0", display: "flex", flexDirection: "column", gap: 0 }}>
                {s.benefits.map((b) => (
                  <li key={b} style={{ display: "flex", gap: 20, alignItems: "flex-start", padding: "20px 0", borderBottom: "1px solid var(--line)" }}>
                    <span style={{ width: 28, height: 28, background: "var(--green)", color: "#fff", borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <span style={{ color: "var(--ink)", fontSize: 18, lineHeight: 1.55, fontWeight: 500 }}>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ position: "relative", minHeight: 480, overflow: "hidden" }}>
              <img src={s.image} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
              <div style={{ position: "absolute", left: 24, bottom: 24, background: "var(--bg)", padding: "12px 18px" }}>
                <div className="eyebrow">{s.code} · {s.title.toUpperCase()}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="section" style={{ background: "var(--ink)", color: "var(--dark-ink)" }}>
        <div className="container-grav">
          <div className="eyebrow" style={{ color: "rgba(246,244,239,0.6)", marginBottom: 20 }}><span className="sep" style={{ background: "rgba(246,244,239,0.6)" }} />Use cases</div>
          <h2 className="display-lg" style={{ color: "var(--dark-ink)", maxWidth: 820, marginBottom: 72 }}>Where this practice is deployed.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0" style={{ border: "1px solid rgba(246,244,239,0.15)" }}>
            {s.useCases.map((u, i) => (
              <div key={u.title} style={{ padding: "40px 32px", borderRight: i < 2 ? "1px solid rgba(246,244,239,0.15)" : "none", minHeight: 280 }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.2em", color: "rgba(246,244,239,0.5)", marginBottom: 20, fontWeight: 600 }}>USE CASE 0{i + 1}</div>
                <h3 className="display-sm" style={{ color: "var(--green)", marginBottom: 14 }}>{u.title}</h3>
                <p style={{ color: "rgba(246,244,239,0.75)", fontSize: 15, lineHeight: 1.65 }}>{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + next */}
      <section className="section">
        <div className="container-grav">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="tile" style={{ padding: "56px 48px", background: "var(--green)", color: "#fff", border: "1px solid var(--green)" }}>
              <div className="eyebrow" style={{ color: "rgba(255,255,255,0.8)", marginBottom: 28 }}>Initiate</div>
              <h3 className="display-md" style={{ color: "#fff" }}>Brief our {s.title.toLowerCase()} team.</h3>
              <p className="lede" style={{ color: "rgba(255,255,255,0.9)", marginTop: 14 }}>Response within one business day.</p>
              <div style={{ marginTop: 36 }}>
                <Link to="/contact" className="btn btn-light">Request consultation <span className="arrow"><ArrowUpRight size={14} /></span></Link>
              </div>
            </div>
            <Link to={`/services/${next.slug}`} className="tile" style={{ padding: "56px 48px", textDecoration: "none", color: "var(--ink)", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <div className="accent-bar" />
              <div>
                <div className="eyebrow">Next capability / {next.code}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 20 }}>
                  <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--green-bg)", color: "var(--green)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    {NextIcon && <NextIcon size={22} strokeWidth={1.75} />}
                  </div>
                  <h3 className="display-sm">{next.title}</h3>
                </div>
                <p className="body-md" style={{ marginTop: 16, maxWidth: 420 }}>{next.tagline}</p>
              </div>
              <div style={{ marginTop: 36, display: "inline-flex", alignItems: "center", gap: 10, color: "var(--ink)", fontWeight: 600, fontSize: 16 }}>
                Explore next practice <ArrowUpRight size={18} />
              </div>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;
